// REST client for the Lastslate collab server (Hocuspocus +
// Fastify on Fly.io). Mirror of src/main/cloud-client.js — same
// endpoints, same envelope shapes, same `{ ok, ... }` / `{ ok: false,
// error }` contract. The only difference is auth: Electron pulls
// JWTs from safeStorage; the web build pulls them from supabase-js's
// session in localStorage.

const REQUEST_TIMEOUT_MS = 8000;

function trimBase(url) {
  return (url || '').replace(/\/$/, '');
}

export function createCloudClient({ supabase, env }) {
  function baseUrl() {
    return trimBase(env?.COLLAB_URL || '');
  }
  function isConfigured() {
    return baseUrl().length > 0;
  }
  async function authHeader() {
    if (!supabase) {
      const e = new Error('not_signed_in');
      e.code = 'not_signed_in';
      throw e;
    }
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (!token) {
      const e = new Error('not_signed_in');
      e.code = 'not_signed_in';
      throw e;
    }
    return `Bearer ${token}`;
  }
  async function request(method, path, body) {
    if (!isConfigured()) return { ok: false, error: 'cloud_not_configured' };
    let headers;
    try {
      headers = { Authorization: await authHeader() };
    } catch (err) {
      return { ok: false, error: err.code || 'auth_error' };
    }
    const init = { method, headers };
    if (body !== undefined) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    const controller = new AbortController();
    init.signal = controller.signal;
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let res;
    try {
      res = await fetch(`${baseUrl()}${path}`, init);
    } catch (err) {
      const aborted = err && (err.name === 'AbortError' || controller.signal.aborted);
      return { ok: false, error: 'network', detail: aborted ? 'timeout' : err.message };
    } finally {
      clearTimeout(timer);
    }
    if (res.status === 204) return { ok: true };
    let payload = null;
    try {
      payload = await res.json();
    } catch {
      /* empty */
    }
    if (!res.ok) {
      const code = (payload && (payload.code || payload.error)) || `http_${res.status}`;
      return { ok: false, error: code, status: res.status, payload };
    }
    return { ok: true, ...payload };
  }

  // ── REST surface, one wrapper per server route. ───────────────────

  return {
    isConfigured,
    baseUrl,
    request,
    async listProjects() {
      return request('GET', '/api/v1/projects');
    },
    async createProject({ title, format = 'feature' } = {}) {
      return request('POST', '/api/v1/projects', { title, format });
    },
    async getProject(projectId) {
      return request('GET', `/api/v1/projects/${encodeURIComponent(projectId)}`);
    },
    async archiveProject(projectId) {
      return request('DELETE', `/api/v1/projects/${encodeURIComponent(projectId)}`);
    },
    async createDraft(projectId, { name, fromDraft, atSeq } = {}) {
      return request('POST', `/api/v1/projects/${encodeURIComponent(projectId)}/drafts`, {
        name,
        fromDraft,
        atSeq,
      });
    },
    async listSnapshots(draftId) {
      return request('GET', `/api/v1/drafts/${encodeURIComponent(draftId)}/snapshots`);
    },
    async labelSnapshot(draftId, label) {
      return request('POST', `/api/v1/drafts/${encodeURIComponent(draftId)}/snapshots`, {
        label,
      });
    },
    async loadDraftState(draftId) {
      return request('GET', `/api/v1/drafts/${encodeURIComponent(draftId)}/state`);
    },
    async saveDraftState(draftId, bytesBase64) {
      return request('POST', `/api/v1/drafts/${encodeURIComponent(draftId)}/state`, {
        bytes: bytesBase64,
      });
    },
    async renameDraft(draftId, name) {
      return request('PATCH', `/api/v1/drafts/${encodeURIComponent(draftId)}`, { name });
    },
    async archiveDraft(draftId) {
      return request('DELETE', `/api/v1/drafts/${encodeURIComponent(draftId)}`);
    },
  };
}
